/**
 * Variables, constants and units per context (contract `ExprVarSet`; D§4.11, D§5.8, W1-10c core-expr).
 *
 *   field         v i n u                       any number field (line fields, draw order `i + 10`, ...)
 *   noteField     v i n u t s x speed size      note fields: the note's beat, seconds, X, speed and width
 *   keyField      v i n u t s                   key values (value / arrive / leave); speed unit `v` allowed
 *   segmentField  v i n u t s v0 v1 len         segment values: start/end value and length in beats; `v` unit
 *   motionPath    t beat                        motion path x(t), y(t), r(t): t = progress 0..1, beat = beat
 *   table         every item variable           tables (P1); `v` unit allowed
 * Constants `pi` and `e` everywhere. Unknown identifiers are parse errors.
 *
 * Scope mapping: names read the ExprScope field of the same name, except in `motionPath`, where `t` (path
 * progress) reads `scope.u` and `beat` reads `scope.t` (see `motionPathScope` in index.ts).
 */

import { EXPR_HELP, type ExprHelpKey } from './errors';
import type { Kind, ScopeKey } from './ir';
import type { ExprVarSet } from './types';

export interface VarSpec {
  /** Name as written in expressions. */
  readonly name: string;
  /** Scope field it reads. */
  readonly key: ScopeKey;
  readonly kind: Kind;
  /**
   * A value of the item itself (its current value, the segment's start and end values, a note's X, speed or
   * width), as opposed to its position in the selection (i, n, u) or its time. Such a value is absolute: a
   * speed-unit distance added to it is not measured from the segment start (`= v0 + 3v` = `= 3v`), and a
   * percentage multiplying it is a fraction of it (`v * 120%`).
   */
  readonly itemValue: boolean;
  readonly desc: ExprHelpKey;
}

function spec(name: string, key: ScopeKey, kind: Kind, desc: ExprHelpKey, itemValue = false): VarSpec {
  return { name, key, kind, itemValue, desc };
}

const V = spec('v', 'v', 'num', EXPR_HELP['var.v'], true);
const I = spec('i', 'i', 'num', EXPR_HELP['var.i']);
const N = spec('n', 'n', 'num', EXPR_HELP['var.n']);
const U = spec('u', 'u', 'num', EXPR_HELP['var.u']);
const T = spec('t', 't', 'beat', EXPR_HELP['var.t']);
const S = spec('s', 's', 'time', EXPR_HELP['var.s']);
const X = spec('x', 'x', 'num', EXPR_HELP['var.x'], true);
const SPEED = spec('speed', 'speed', 'num', EXPR_HELP['var.speed'], true);
const SIZE = spec('size', 'size', 'num', EXPR_HELP['var.size'], true);
const V0 = spec('v0', 'v0', 'num', EXPR_HELP['var.v0'], true);
const V1 = spec('v1', 'v1', 'num', EXPR_HELP['var.v1'], true);
const LEN = spec('len', 'len', 'beat', EXPR_HELP['var.len']);
const PATH_T = spec('t', 'u', 'num', EXPR_HELP['var.pathT']);
const PATH_BEAT = spec('beat', 't', 'beat', EXPR_HELP['var.pathBeat']);

const ITEM = [V, I, N, U] as const;

export const VAR_SETS: Readonly<Record<ExprVarSet, readonly VarSpec[]>> = {
  field: [...ITEM],
  noteField: [...ITEM, T, S, X, SPEED, SIZE],
  keyField: [...ITEM, T, S],
  segmentField: [...ITEM, T, S, V0, V1, LEN],
  motionPath: [PATH_T, PATH_BEAT],
  table: [...ITEM, T, S, X, SPEED, SIZE, V0, V1, LEN],
};

/** Contexts whose values are motion values, where the RPE speed unit `v` is allowed. */
export const SPEED_UNIT_SETS: ReadonlySet<ExprVarSet> = new Set<ExprVarSet>(['keyField', 'segmentField', 'table']);

export interface ConstSpec {
  readonly name: string;
  readonly value: number;
  readonly desc: ExprHelpKey;
}

export const CONSTANTS: readonly ConstSpec[] = [
  { name: 'pi', value: Math.PI, desc: EXPR_HELP['const.pi'] },
  { name: 'e', value: Math.E, desc: EXPR_HELP['const.e'] },
];

/** Every variable name of any context (for "not available here" versus "unknown name"). */
export const ALL_VAR_NAMES: ReadonlySet<string> = new Set(
  Object.values(VAR_SETS).flatMap((set) => set.map((v) => v.name)),
);

/** Lower-cases a name and maps `π` to `pi` (names are case-insensitive). */
export function canonicalName(name: string): string {
  return name === 'π' ? 'pi' : name.toLowerCase();
}

export function findVar(set: ExprVarSet, name: string): VarSpec | undefined {
  const n = canonicalName(name);
  return VAR_SETS[set].find((v) => v.name === n);
}

export function findConst(name: string): ConstSpec | undefined {
  const n = canonicalName(name);
  return CONSTANTS.find((c) => c.name === n);
}

/** Variable names usable in a context, in display order. */
export function varNames(set: ExprVarSet): readonly string[] {
  return VAR_SETS[set].map((v) => v.name);
}

export type UnitName = 'px' | '%' | 'deg' | 's' | 'ms' | 'b' | 'v';

export interface UnitSpec {
  readonly unit: UnitName;
  readonly desc: ExprHelpKey;
}

export const UNITS: readonly UnitSpec[] = [
  { unit: 'px', desc: EXPR_HELP['unit.px'] },
  { unit: '%', desc: EXPR_HELP['unit.pct'] },
  { unit: 'deg', desc: EXPR_HELP['unit.deg'] },
  { unit: 's', desc: EXPR_HELP['unit.s'] },
  { unit: 'ms', desc: EXPR_HELP['unit.ms'] },
  { unit: 'b', desc: EXPR_HELP['unit.b'] },
  { unit: 'v', desc: EXPR_HELP['unit.v'] },
];

/** Canonical unit for a suffix as written (case-insensitive), or null for an unknown suffix. */
export function unitOf(suffix: string): UnitName | null {
  const u = suffix.toLowerCase();
  if (u === '°') return 'deg';
  switch (u) {
    case 'px':
    case '%':
    case 'deg':
    case 's':
    case 'ms':
    case 'b':
    case 'v':
      return u;
    default:
      return null;
  }
}
